import round from 'lodash/round';

import React from 'react';
import PropTypes from 'prop-types';

import './styles.styl';

/* ------------------------
 * Image Crop component.
 *
 * Code highly inspired in https://github.com/DominicTobias/react-image-crop
 *
      Selection area:

    x1y1-------------+
      |              |
      |              |
      +-------------x2y2

--------------------------- */

const zero = 0;
const hundred = 100;
const DEFINING_CROP_AREA = 1;
const DRAGGING_CROP_AREA = 2;
const MODIFYING_CROP_AREA = 3;

/**
 * @param  {object{x, y, width, height}}  crop
 * @return {bool}
 */
function isCropValid(crop) {
    return (
        crop &&
        crop.width &&
        crop.height &&
        crop.x >= zero &&
        crop.y >= zero &&
        crop.x <= hundred &&
        crop.y <= hundred &&
        crop.x + crop.width <= hundred &&
        crop.y + crop.height <= hundred
    );
}

/**
 * Returns element offset on page.
 * @param  {DOMNode} el
 * @return {object{top, left}}
 */
function getElementOffset(el) {
    const rect = el.getBoundingClientRect();
    const docEl = document.documentElement;

    const rectTop = rect.top + window.pageYOffset - docEl.clientTop;
    const rectLeft = rect.left + window.pageXOffset - docEl.clientLeft;

    return {
        top: rectTop,
        left: rectLeft,
    };
}

/**
 * Clamps given number between 0 and 100.
 * @param  {number} number
 * @return {number}
 */
function clamp(number) {
    return Math.max(zero, Math.min(number, hundred));
}

/**
 * @param  {object{x, y, width, height}} crop
 * @return {object{x, y, width, height}}
 */
function clampCrop(crop) {
    let { x, y, width, height } = crop;

    x = clamp(x);
    y = clamp(y);

    if (x + width > hundred) {
        width = hundred - x;
    }
    if (y + height > hundred) {
        height = hundred - y;
    }

    return {
        x,
        y,
        width,
        height,
    };
}

/**
 * Turn the 2-point rectangle format into the crop format.
 * @param  {object{x1, y1, x2, y2}} rectangle
 * @return {object{x, y, width, height}}
 */
function getCropFrom2PointRectangle(rectangle) {
    const { x1, x2, y1, y2 } = rectangle;

    return {
        x: round(x2 > x1 ? x1 : x2, 1),
        y: round(y2 > y1 ? y1 : y2, 1),
        width: round(x2 > x1 ? x2 - x1 : x1 - x2, 1),
        height: round(y2 > y1 ? y2 - y1 : y1 - y2, 1),
    };
}

const baseClassName = 'ImageCrop';

export default class ImageCrop extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        src: PropTypes.string.isRequired,
        crop: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            width: PropTypes.number,
            height: PropTypes.number,
        }),
        initialCrop: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            width: PropTypes.number,
            height: PropTypes.number,
        }),
    };

    static defaultProps = {
        className: undefined,
        crop: undefined,
        initialCrop: undefined,
    };

    constructor(props) {
        super(props);

        // note: this is fine if it's undefined. don't think it's required.
        const propCrop = props.crop || props.initialCrop;

        this.state = {
            crop: propCrop ? clampCrop(propCrop) : undefined,
            aspectRatio: null,
            imageDimensions: {},
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    /**
     * Returns the current crop in the state.
     * Used by the outter component to obtain crop, for example, when hitting a save button.
     * @return {object{x, y, width, height}}
     */
    getCrop() {
        return this.state.crop ? Object.assign({}, this.state.crop) : undefined;
    }

    /**
     * Returns the relative position in percentages to the component from the React event data.
     * @param  {object} event
     * @return {object{x, y}}
     */
    getRelativePosition(event) {
        const imageOffset = getElementOffset(this.imageRef.current);
        const x = ((event.pageX - imageOffset.left) / this.imageRef.current.width) * hundred;

        const y = ((event.pageY - imageOffset.top) / this.imageRef.current.height) * hundred;
        return { x, y };
    }

    /**
     * Happens on mouse down when starting to draw a new crop area.
     * @param  {object{x, y}} position
     */
    startDefiningCropArea(position) {
        this.mouseDownMode = DEFINING_CROP_AREA;
        this.dragData = {
            rect: {
                x1: position.x,
                y1: position.y,
                x2: position.x,
                y2: position.y,
            },
        };

        // Reset any previous crop area.
        this.setState({
            crop: undefined,
        });
    }

    /**
     * Happens on mouse move when defining the crop area initially.
     * @param  {object{x, y}} position
     */
    continueDefiningCropArea(position) {
        this.dragData.rect.x2 = position.x;
        this.dragData.rect.y2 = position.y;
    }

    /**
     * Happens on mouse down when dragging a previously defined crop area.
     * @param  {object{x, y}} position
     */
    startDraggingCropArea(position) {
        this.mouseDownMode = DRAGGING_CROP_AREA;

        this.dragData = {
            rect: {
                x1: this.state.crop.x,
                y1: this.state.crop.y,
                x2: this.state.crop.x + this.state.crop.width,
                y2: this.state.crop.y + this.state.crop.height,
            },
            prevX: position.x,
            prevY: position.y,
        };
    }

    /**
     * Happens on mouse move when draging the entire crop area.
     * @param  {object{x, y}} position
     */
    continueDraggingCropArea(position) {
        const diffX = position.x - this.dragData.prevX;
        const diffY = position.y - this.dragData.prevY;

        this.dragData.rect.x1 += diffX;
        this.dragData.rect.x2 += diffX;
        this.dragData.rect.y1 += diffY;
        this.dragData.rect.y2 += diffY;

        this.dragData.prevX = position.x;
        this.dragData.prevY = position.y;
    }

    /**
     * Happens on mouse down when selecting the handles to change size of crop area.
     * @param  {object{x, y}} position
     * @param  {string} handleAttr
     */
    startModifyingCropArea(position, handleAttr) {
        this.mouseDownMode = MODIFYING_CROP_AREA;

        this.dragData = {
            rect: {
                x1: this.state.crop.x,
                y1: this.state.crop.y,
                x2: this.state.crop.x + this.state.crop.width,
                y2: this.state.crop.y + this.state.crop.height,
            },
            handleAttr,
        };
    }

    /**
     * Happens on mouse move when dragging the handles to change size of crop area.
     * @param  {object{x, y}} position
     */
    continueModifyingCropArea(position) {
        const { handleAttr } = this.dragData;

        if (handleAttr.indexOf('t') !== -1) {
            this.dragData.rect.y1 = position.y;
        }
        if (handleAttr.indexOf('b') !== -1) {
            this.dragData.rect.y2 = position.y;
        }
        if (handleAttr.indexOf('l') !== -1) {
            this.dragData.rect.x1 = position.x;
        }
        if (handleAttr.indexOf('r') !== -1) {
            this.dragData.rect.x2 = position.x;
        }
    }

    onMouseDown = (event) => {
        // Only capture click if it's with left mouse button.
        if (event.button !== 0) {
            return;
        }
        event.preventDefault(); // prevent default selection effect.

        const position = this.getRelativePosition(event);

        // dragging selection rectangle.
        if (event.target === this.cropSelectionRef.current) {
            this.startDraggingCropArea(position);
            // modyfing selected area.
        } else if (event.target.dataset.dir) {
            this.startModifyingCropArea(position, event.target.dataset.dir);
            // Initiating a new drag.
        } else {
            this.startDefiningCropArea(position);
        }
    };

    onMouseUp = (event) => {
        this.mouseDownMode = null;
        this.dragData = {};
    };

    onMouseMove = (event) => {
        // only if mouse went down, we update the crop area.
        if (!this.mouseDownMode) {
            return;
        }
        const position = this.getRelativePosition(event);

        if (this.mouseDownMode === DEFINING_CROP_AREA) {
            this.continueDefiningCropArea(position);
        } else if (this.mouseDownMode === DRAGGING_CROP_AREA) {
            this.continueDraggingCropArea(position);
        } else if (this.mouseDownMode === MODIFYING_CROP_AREA) {
            this.continueModifyingCropArea(position);
        }

        const crop = getCropFrom2PointRectangle(this.dragData.rect);

        // Crop could be invalid if crop area would leave the image area.
        // In that case we don't update the state so the view and crop area stay the same.
        if (isCropValid(crop)) {
            this.setState({ crop });
        }
    };

    onImageLoad = (event) => {
        const imageNode = event.target;
        const aspectRatio = imageNode.width / imageNode.height;

        // Calculating desired image dimensions based on aspect ratio and available space.
        const imageDimensions = this.calculateImageDimensionProperties(aspectRatio);

        this.setState({
            aspectRatio,
            imageDimensions,
        });
    };

    /**
     * Update image dimensions in state on resize.
     */
    handleResize = (event) => {
        const imageDimensions = this.calculateImageDimensionProperties(this.state.aspectRatio);
        this.setState({
            imageDimensions,
        });
    };

    /**
     * We need to make calculations to obtain desired dimensions of image and crop wrapper, so that
     * they adjust to all available space while 1) keeping aspect ratio and 2) not overflowing crop
     * wrapper outside image.
     *
     * @param  {number} aspectRatio of image, calculated when loaded.
     * @return {object {width, height}}
     */
    calculateImageDimensionProperties(aspectRatio) {
        if (!aspectRatio || !this.wrapperRef.current) {
            return {};
        }

        const wrapperRect = this.wrapperRef.current.getBoundingClientRect();

        const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;
        let height;
        let width;

        // image wider than container area.
        if (aspectRatio > wrapperAspectRatio) {
            ({ width } = wrapperRect);
            height = wrapperRect.width / aspectRatio;
            // container area wider than image.
        } else {
            width = wrapperRect.height * aspectRatio;
            ({ height } = wrapperRect);
        }

        return { width, height };
    }

    wrapperRef = React.createRef();
    cropSelectionRef = React.createRef();
    imageRef = React.createRef();

    /**
     * Returns CSS style rules for the given crop.
     * @param  {object {x, y, width, height}} crop
     * @return {object {top, left, width, height}}
     */
    getCropStyle(crop) {
        return {
            top: `${crop.y}%`,
            left: `${crop.x}%`,
            width: `${crop.width}%`,
            height: `${crop.height}%`,
        };
    }

    createCropSelection(crop) {
        const style = this.getCropStyle(crop);
        return (
            <div style={style} className={`${baseClassName}_crop-selection`} ref={this.cropSelectionRef}>
                <div className={`${baseClassName}_drag-bar ord-t`} data-dir="t" />
                <div className={`${baseClassName}_drag-bar ord-r`} data-dir="r" />
                <div className={`${baseClassName}_drag-bar ord-b`} data-dir="b" />
                <div className={`${baseClassName}_drag-bar ord-l`} data-dir="l" />

                <div className={`${baseClassName}_drag-handle ord-tl`} data-dir="tl" />
                <div className={`${baseClassName}_drag-handle ord-t`} data-dir="t" />
                <div className={`${baseClassName}_drag-handle ord-tr`} data-dir="tr" />
                <div className={`${baseClassName}_drag-handle ord-r`} data-dir="r" />
                <div className={`${baseClassName}_drag-handle ord-br`} data-dir="br" />
                <div className={`${baseClassName}_drag-handle ord-b`} data-dir="b" />
                <div className={`${baseClassName}_drag-handle ord-bl`} data-dir="bl" />
                <div className={`${baseClassName}_drag-handle ord-l`} data-dir="l" />
            </div>
        );
    }

    render() {
        const { src, className } = this.props;

        const { imageDimensions } = this.state;

        const crop = this.props.crop || this.state.crop;
        const cropSelection = crop ? this.createCropSelection(crop, baseClassName) : undefined;

        return (
            <div
                className={[baseClassName, className].filter(Boolean).join(' ')}
                onMouseDown={this.onMouseDown}
                onMouseMove={this.onMouseMove}
                onMouseUp={this.onMouseUp}
                ref={this.wrapperRef}
            >
                <div className={`${baseClassName}_image-wrapper`} style={imageDimensions}>
                    <img
                        ref={this.imageRef}
                        className={`${baseClassName}_image`}
                        src={src}
                        onLoad={this.onImageLoad}
                        style={imageDimensions}
                    />
                    <div className={`${baseClassName}_crop-wrapper`}>{cropSelection}</div>
                </div>
            </div>
        );
    }
}
